// src/ui/ReplyQuote.jsx — the quote block inside a message bubble.
//
// A port of the app's ReplyQuote.tsx: coloured left rail, sender name, a right-aligned
// TYPE badge, a 38px thumbnail on the left, and the quoted line. The app's version is
// 100% inline-styled with no StyleSheet; this one is class-based so it can inherit the
// bubble's own colours through `currentColor`, which is how it stays legible on an
// outgoing bubble, an incoming one, and in dark mode without three sets of hex values.
//
// 🟥 EVERYTHING RICH COMES FROM THE ORIGINAL, NOT THE QUOTE. The stored snapshot holds
//    a type, a line of text and (for photos) an 80px thumbnail — no duration, no page
//    count, no poll options. When the original message is still in the loaded window we
//    read those from it; when it isn't, the quote degrades to the snapshot rather than
//    inventing anything.

import React from 'react';
import {
  Image as ImageIcon, Video, Mic, DocText, MapPin, Poll, Plane, Phone, Person, Smile, Play,
} from './icons';
import { replyKind, REPLY_ACCENT, REPLY_LABEL, isRedacted } from '../services/replies';
import { docStyle } from '../services/documents';

const KIND_ICON = {
  image: ImageIcon, sticker: Smile, video: Video, images: ImageIcon, audio: Mic,
  document: DocText, location: MapPin, contact: Person, poll: Poll, flight: Plane,
  shared_post: ImageIcon, link: ImageIcon, call: Phone,
};

/** The quoted line, preferring live detail from the original when we have it. */
function bodyFor(kind, rt, original) {
  const o = original || {};
  switch (kind) {
    case 'audio': {
      const secs = Number(o.duration);
      return Number.isFinite(secs) && secs > 0
        ? `Voice message · ${Math.floor(secs / 60)}:${String(Math.round(secs % 60)).padStart(2, '0')}`
        : 'Voice message';
    }
    case 'document': return o.documentName || stripLabel(rt.text) || 'Document';
    case 'poll': return o._payload?.question || stripLabel(rt.text) || 'Poll';
    case 'flight': {
      const f = o.flightData;
      if (f?.origin?.code && f?.destination?.code) return `${f.origin.code} → ${f.destination.code}`;
      return stripLabel(rt.text) || 'Flight';
    }
    case 'location': return o._payload?.name || 'Location';
    case 'contact': return o._payload?.name || stripLabel(rt.text) || 'Contact';
    case 'call': return rt.callData?.callType === 'video' ? 'Video call' : 'Voice call';
    default: {
      // 🟢 a still-sealed quote must never render as a bare padlock — the type label
      //    says more, and it's what the app falls back to as well
      /* 💬 a long quote travels CUT (only its start is sealed, once per device —
            services/sealDevices): the original, when it is here, is the whole of it */
      if (rt.quoteCut && typeof o.text === 'string' && o.text && !isRedacted(o.text)) return o.text;
      if (isRedacted(rt.text)) return REPLY_LABEL[kind] || 'Message';
      return rt.text;
    }
  }
}
/** "📄 Document: report.pdf" → "report.pdf" — the emoji label is already the badge. */
const stripLabel = (s) => (isRedacted(s) ? '' : String(s || '').replace(/^[^\s]*\s*\w[\w\s]*:\s*/u, '').trim());

export default function ReplyQuote({ replyTo, original, name, onJump }) {
  if (!replyTo) return null;
  const kind = replyKind(replyTo, original);
  const accent = kind === 'call'
    ? (replyTo.callData?.outcome === 'missed' ? '#E53935' : '#4CAF50')
    : (REPLY_ACCENT[kind] || null);
  const Icon = KIND_ICON[kind];
  const label = REPLY_LABEL[kind];

  const thumb = replyTo.thumbnailBase64
    ? `data:image/jpeg;base64,${replyTo.thumbnailBase64}`
    : (original?._media && ['image', 'sticker', 'gif'].includes(original.type) ? original._media : null);
  const docTint = kind === 'document'
    ? docStyle(original?.documentName || replyTo.text || '').color : null;
  const count = replyTo.mediaCount || (Array.isArray(original?.images) ? original.images.length : 0);
  const playBadge = kind === 'video' || (kind === 'images' && replyTo.hasVideo);

  return (
    <button
      type="button"
      className="rq"
      onClick={(e) => { e.stopPropagation(); onJump?.(replyTo.messageId); }}
      style={accent ? { '--rq-accent': accent } : undefined}
      title="Go to the original message"
    >
      <span className="rq-rail" />
      <span className="rq-body">
        <span className="rq-head">
          {Icon && <Icon width={11} height={11} className="rq-kico" />}
          <span className="rq-name">{name || 'Message'}</span>
          {label && <span className="rq-badge">{label}</span>}
        </span>

        <span className="rq-line">
          {thumb ? (
            <span className="rq-thumb">
              <img src={thumb} alt="" className={kind === 'sticker' ? 'contain' : ''} />
              {playBadge && <span className="rq-play"><Play width={11} height={11} /></span>}
              {count > 1 && <span className="rq-count">{count}</span>}
            </span>
          ) : Icon ? (
            <span
              className={`rq-tile ${kind === 'contact' ? 'round' : ''} ${kind === 'document' ? 'doc' : ''}`}
              style={docTint ? { '--rq-accent': docTint } : undefined}
            >
              <Icon width={18} height={18} />
            </span>
          ) : null}
          <span className="rq-text">{bodyFor(kind, replyTo, original)}</span>
        </span>
      </span>
    </button>
  );
}
